/**
 * Nano ID Generator Tool
 */

import { customAlphabet } from 'nanoid';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { IGeneratorTool } from '@/interfaces/ITool';

export const NanoIdGeneratorTool: IGeneratorTool = {
  id: 'nanoid-generator',
  name: 'Nano ID Generator',
  description: 'Generate URL-friendly random IDs',
  version: '1.0.0',
  type: 'generator',
  category: 'Generators',
  keywords: ['nanoid', 'id', 'generate', 'random', 'uuid'],

  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'count',
      type: 'number',
      label: 'Count',
      defaultValue: 10,
      validation: { min: 1, max: 200, step: 1 },
      description: 'Number of IDs to generate',
    },
    {
      name: 'size',
      type: 'number',
      label: 'Size',
      defaultValue: 21,
      validation: { min: 4, max: 128, step: 1 },
      description: 'Length of each ID',
    },
    {
      name: 'alphabet',
      type: 'string',
      label: 'Alphabet',
      defaultValue: '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ_-',
      description: 'Characters used for generation',
    },
  ],

  async generate(config: Record<string, unknown>): Promise<string> {
    const count = typeof config.count === 'number' ? (config.count as number) : 10;
    const size = typeof config.size === 'number' ? (config.size as number) : 21;
    const alphabet = String(config.alphabet ?? '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ_-');

    if (alphabet.length < 2) throw ToolError.validation('Alphabet must have at least 2 characters');

    const generateOne = customAlphabet(alphabet, size);
    const ids = Array.from({ length: count }, () => generateOne());
    return ids.join('\n');
  },
};
